import React, { useState, useEffect } from 'react';
import {
  Buildings, TreeStructure, CalendarBlank, Clock, ArrowRight, PencilSimple, ClockCounterClockwise,
  Info, ArrowClockwise, X, Check,
} from '@phosphor-icons/react';
import {
  getBranchNames, getGroupCode, getAuctionSchedule, updateAuctionSchedule, saveData,
  getEmployeeCodes,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { pickerOnly } from '../utils/pickerOnly';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

const AUCTION_DATE_WINDOW_DAYS = 7;

function toDateString(value) {
  if (!value) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().substring(0, 10);
  }
  const text = String(value).trim();

  const yearFirst = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (yearFirst) return `${yearFirst[1]}-${yearFirst[2]}-${yearFirst[3]}`;

  const dayFirst = text.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dayFirst) {
    let [, day, month] = dayFirst;
    const year = dayFirst[3];
    if (Number(month) > 12 && Number(day) <= 12) [day, month] = [month, day];
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return '';
}

function toDisplayDate(isoDate) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate || '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : (isoDate || '');
}

function toTimeString(value) {
  if (!value) return '';
  const m = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  return `${m[1].padStart(2, '0')}:${m[2]}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d;
}

function getNewAuctionDateError(oldDate, newDate) {
  if (!newDate) return 'required';
  if (!oldDate) return null;

  const newD = new Date(newDate);
  const minD = addDays(oldDate, -AUCTION_DATE_WINDOW_DAYS);
  const maxD = addDays(oldDate, AUCTION_DATE_WINDOW_DAYS);
  newD.setHours(0, 0, 0, 0);
  minD.setHours(0, 0, 0, 0);
  maxD.setHours(0, 0, 0, 0);

  if (newD < minD) return 'tooEarly';
  if (newD > maxD) return 'tooLate';

  return null;
}

// Auction Hall is a single hall number from 1 to 5.
const HALL_PATTERN = /^[1-5]$/;
// While typing: drop anything outside 1-5 and keep the latest key pressed.
const typedHall = (v) => String(v ?? '').replace(/[^1-5]/g, '').slice(-1);
// From the saved schedule: use it only when it is already a valid hall number.
const savedHall = (v) => (HALL_PATTERN.test(String(v ?? '').trim()) ? String(v).trim() : '');

function AuctionDateTimeForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [groupList, setGroupList] = useState([]);

  const [cao, setCao] = useState('');
  const [branchschema, setBranchschema] = useState(null);

  const [group, setGroup] = useState('');
  const [chitgroupId, setChitgroupId] = useState(null);

  const [oldDate, setOldDate] = useState('');
  const [oldTime, setOldTime] = useState('');

  const [auctionerName, setAuctionerName] = useState('');
  const [auctionHall, setAuctionHall] = useState('');
  const [minAuctionDate, setMinAuctionDate] = useState('');
  const [maxAuctionDate, setMaxAuctionDate] = useState('');

  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [reason, setReason] = useState('');

  // New auctioner picker (employee master). The option value is the contact_id; the
  // dropdown shows the employee code and the read-only box shows the name.
  const [employees, setEmployees] = useState([]);
  const [newAuctionerId, setNewAuctionerId] = useState('');
  const [newAuctionerName, setNewAuctionerName] = useState('');
  const [newAuctionHall, setNewAuctionHall] = useState('');

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingOldDate, setPendingOldDate] = useState('');
  const [pendingOldTime, setPendingOldTime] = useState('');
  const [pendingNewDate, setPendingNewDate] = useState('');
  const [pendingNewTime, setPendingNewTime] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
  }, [formKey]);

  useEffect(() => {
    getEmployeeCodes().then((res) => setEmployees(res || [])).catch(() => setEmployees([]));
  }, []);

  const resetFromGroup = () => {
    setChitgroupId(null);
    setOldDate('');
    setOldTime('');
    setAuctionerName('');
    setAuctionHall('');
    setMinAuctionDate('');
    setMaxAuctionDate('');
    setNewDate('');
    setNewTime('');
    setNewAuctionerId('');
    setNewAuctionerName('');
    setNewAuctionHall('');
  };

  const handleAuctionerChange = (value) => {
    setNewAuctionerId(value ? String(value) : '');
    const picked = employees.find((x) => String(x.contact_id) === String(value));
    setNewAuctionerName(picked?.business_reference_name || '');
  };

  const resetFromBranch = () => {
    setBranchschema(null);
    setGroupList([]);
    setGroup('');
    resetFromGroup();
  };

  const setAuctionDateLimits = (oldDateValue) => {
    if (!oldDateValue) return;
    setMinAuctionDate(toDateString(addDays(oldDateValue, -AUCTION_DATE_WINDOW_DAYS)));
    setMaxAuctionDate(toDateString(addDays(oldDateValue, AUCTION_DATE_WINDOW_DAYS)));
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_code ?? selected.branchCode ?? selected.schema_name ?? selected.schemaName ?? null;
    setBranchschema(schema);

    try {
      const res = await getGroupCode(schema);
      setGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setGroupList([]);
    }
  };

  const handleGroupChange = async (chitgroupIdValue) => {
    setGroup(chitgroupIdValue);
    resetFromGroup();
    if (!chitgroupIdValue) return;

    const selected = groupList.find((g) => String(g.chitgroup_id) === String(chitgroupIdValue));
    if (!selected) return;

    const idNum = selected.chitgroup_id;
    setChitgroupId(idNum);

    try {
      const res = await getAuctionSchedule(branchschema, idNum);
      if (res && res.length > 0) {
        const data = res[0];
        const formattedDate = toDateString(data.auction_date);
        const formattedTime = toTimeString(data.auction_time);
        setOldDate(formattedDate);
        setOldTime(formattedTime);
        setAuctionerName(data.contact_mailing_name || '');
        setAuctionHall(data.auction_hall || '');
        setAuctionDateLimits(formattedDate);
        setNewDate(formattedDate);
        setNewTime(formattedTime);
      } else {
        setOldDate('');
        setOldTime('');
        setAuctionerName('');
        setAuctionHall('');
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      }
    } catch {
      setOldDate('');
      setOldTime('');
      setAuctionerName('');
      setAuctionHall('');
    }
  };

  const newDateError = touched ? getNewAuctionDateError(oldDate, newDate) : null;
  const dateInvalid = !!newDateError;
  const timeInvalid = touched && !timePattern.test(newTime);
  const auctionerInvalid = touched && !newAuctionerId;
  const hallInvalid = touched && !HALL_PATTERN.test(newAuctionHall);
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!cao && !!group
    && !!newAuctionerId && HALL_PATTERN.test(newAuctionHall)
    && !!newDate && !getNewAuctionDateError(oldDate, newDate)
    && timePattern.test(newTime)
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid || !branchschema || !chitgroupId) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    const newDateStr = toDateString(newDate);

    const sameAuctioner = newAuctionerName.trim().toLowerCase() === auctionerName.trim().toLowerCase();
    if (oldDate === newDateStr && oldTime === newTime && sameAuctioner && newAuctionHall === savedHall(auctionHall)) {
      showToast('warn', 'No Changes', 'No changes detected in date, time, auctioner or hall.');
      return;
    }

    const trimmedReason = reason.trim();

    setPendingOldDate(toDisplayDate(oldDate) || '—');
    setPendingOldTime(oldTime || '—');
    setPendingNewDate(toDisplayDate(newDateStr));
    setPendingNewTime(newTime);
    setPendingReason(trimmedReason);

    setPendingPayload({
      branch_code: branchschema,
      chitgroup_id: chitgroupId,
      newauctiondate: newDateStr,
      newauctiontime: newTime,
      newauctionhall: newAuctionHall,
      contact_id: Number(newAuctionerId),
    });

    setPendingAudit({
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'AUCTION_DATE_TIME_CHANGE',
      // Everything the save writes: date, time, auctioner (conducted_id) and hall.
      oldData: { date: oldDate, time: oldTime, auctionerName, auctionHall },
      newData: {
        date: newDateStr,
        time: newTime,
        auctionerName: newAuctionerName,
        auctionerContactId: Number(newAuctionerId),
        auctionHall: newAuctionHall,
      },
      reason: trimmedReason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingPayload(null);
    setPendingAudit(null);
  };

  const confirmSave = async () => {
    if (!pendingPayload || !pendingAudit) return;

    setSaving(true);
    try {
      await updateAuctionSchedule(pendingPayload);
      try {
        await saveData([pendingAudit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Auction schedule updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Schedule updated, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', 'Could not update the auction schedule. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setNewDate('');
    setNewTime('');
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="auction-datetime-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="auction-datetime-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarBlank size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Scheduling</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Auction DateTime</div>
            </div>
          </div>
        </div>

        {/* SECTION A: Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Selection</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the CAO branch and chit group</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select CAO…"
              emptyMessage="No branches found"
              error={touched && !cao}
              testId="auction-cao-select"
            />
            {touched && !cao && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <TreeStructure size={13} /> Group <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={groupList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.chitgroup_id}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group…"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={touched && !group}
              testId="auction-group-select"
            />
            {touched && !group && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION B: Schedule Change */}
        <SectionLock locked={!chitgroupId} hint="Select a CAO and group in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Schedule Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Current schedule is read-only; enter the updated date and time</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
              <ClockCounterClockwise size={13} /> Previous Schedule
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Old Date</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={toDisplayDate(oldDate)} readOnly placeholder="—" data-testid="auction-old-date" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Old Time</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={oldTime || ''} readOnly placeholder="—" data-testid="auction-old-time" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Auctioner Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={auctionerName || ''} readOnly placeholder="—" data-testid="auction-auctioner-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Auction Hall</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={auctionHall || ''} readOnly placeholder="—" data-testid="auction-auction-hall" />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '28px', color: '#9CA3AF' }}>
            <ArrowRight size={18} />
            <span style={{ fontSize: '10px', marginTop: '2px' }}>Update</span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>
              <PencilSimple size={13} /> New Schedule
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>EDITABLE</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Date <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="date"
                  className="input-field st-datepicker"
                  style={{ fontSize: '13px', borderColor: dateInvalid ? '#DC2626' : undefined }}
                  value={newDate}
                  min={minAuctionDate || undefined}
                  max={maxAuctionDate || undefined}
                  disabled={!chitgroupId}
                  onChange={(e) => setNewDate(e.target.value)}
                  {...pickerOnly({ disabled: !chitgroupId })}
                  data-testid="auction-new-date"
                />
                {newDateError === 'required' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
                {newDateError === 'tooEarly' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New date cannot be more than {AUCTION_DATE_WINDOW_DAYS} days before {oldDate}.</div>}
                {newDateError === 'tooLate' && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>New date cannot be more than {AUCTION_DATE_WINDOW_DAYS} days after {oldDate}.</div>}
                {!newDateError && minAuctionDate && maxAuctionDate && (
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }} data-testid="auction-date-window">
                    Allowed: {minAuctionDate} to {maxAuctionDate}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Time <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  type="time"
                  className="input-field st-datepicker"
                  style={{ fontSize: '13px', borderColor: timeInvalid ? '#DC2626' : undefined }}
                  value={newTime}
                  disabled={!chitgroupId}
                  onChange={(e) => setNewTime(e.target.value)}
                  {...pickerOnly({ disabled: !chitgroupId })}
                  data-testid="auction-new-time"
                />
                {timeInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required, format must be HH:MM</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Auctioner Code <span style={{ color: '#DC2626' }}>*</span></label>
                <SearchableSelect
                  key={`auctioner-${formKey}`}
                  options={employees}
                  getLabel={(o) => String(o.employee_code ?? o.contact_id)}
                  getValue={(o) => String(o.contact_id)}
                  value={newAuctionerId}
                  onChange={handleAuctionerChange}
                  placeholder="Select new auctioner code"
                  emptyMessage="No employees found"
                  disabled={!chitgroupId}
                  error={auctionerInvalid}
                  testId="auction-new-auctioner-code"
                />
                {auctionerInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Auctioner Name</label>
                <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={newAuctionerName} readOnly placeholder="—" data-testid="auction-new-auctioner-name" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New Auction Hall <span style={{ color: '#DC2626' }}>*</span></label>
                <input
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: hallInvalid ? '#DC2626' : undefined }}
                  inputMode="numeric"
                  maxLength={1}
                  value={newAuctionHall}
                  disabled={!chitgroupId}
                  onChange={(e) => setNewAuctionHall(typedHall(e.target.value))}
                  data-testid="auction-new-auction-hall"
                />
                {hallInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
              </div>
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION C: Justification */}
        <SectionLock locked={!chitgroupId || !newDate || !newTime} hint="Enter the new date and time in section B first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Justification</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Provide a reason for rescheduling the auction</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter the reason for changing the auction schedule…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="auction-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Required' : reason.trim().length < 10 ? 'Minimum 10 characters' : 'Maximum 200 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="auction-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="auction-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="auction-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CalendarBlank size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Schedule Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to reschedule this auction. Please review each change before confirming.
              </p>

              <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '6px' }}>
                <CalendarBlank size={13} /> Date
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Date</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldDate}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Date</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewDate}</p>
                </div>
              </div>

              <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '6px' }}>
                <Clock size={13} /> Time
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Time</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingOldTime}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Time</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingNewTime}</p>
                </div>
              </div>

              <p style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '6px' }}>Auctioner</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Auctioner Name</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingAudit?.oldData?.auctionerName || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Auctioner Name</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingAudit?.newData?.auctionerName || '—'}</p>
                </div>
              </div>

              <p style={{ fontSize: '12px', fontWeight: '600', color: '#111827', marginBottom: '6px' }}>Auction Hall</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Old Auction Hall</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingAudit?.oldData?.auctionHall || '—'}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>New Auction Hall</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingAudit?.newData?.auctionHall || '—'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="auction-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="auction-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuctionDateTimeForm;